import { CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSite } from "@/context/SiteContext";
import { API, money } from "@/lib/api";

export default function OrderConfirmation() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { settings } = useSite();
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    API.get(`/orders/${id}`, { params: { email: params.get("email") || "" } })
      .then((r) => setOrder(r.data))
      .catch(() => setFailed(true));
  }, [id, params]);

  const phone = settings?.business?.phone;

  return (
    <div className="max-w-2xl mx-auto px-4 py-16" data-testid="order-confirmation-page">
      <Seo title="Order Confirmation | Eileen Jenkins Florist" />
      <div className="text-center mb-8">
        <CheckCircle2 className="h-14 w-14 mx-auto text-primary" />
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-4">Thank you for your order</h1>
        <p className="text-muted-foreground mt-3">
          {order
            ? `Order ${order.order_number} has been received. A confirmation email is on its way to ${order.customer.email}.`
            : "Your order has been received."}
        </p>
      </div>

      {!order && !failed && <Skeleton className="h-48 rounded-xl" />}

      {failed && (
        <Card data-testid="order-lookup-failed">
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            We couldn't load the order details here, but don't worry — your order was placed.
            {phone ? <> If you have any questions, call us on <strong className="text-foreground">{phone}</strong>.</> : null}
          </CardContent>
        </Card>
      )}

      {order && (
        <Card data-testid="order-summary-card">
          <CardContent className="p-6">
            <div className="flex justify-between text-sm mb-4">
              <span className="text-muted-foreground">Order number</span>
              <span className="font-semibold" data-testid="confirm-order-number">{order.order_number}</span>
            </div>
            <div className="space-y-2 text-sm border-t pt-4">
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between gap-4">
                  <span>{it.name}{it.variation ? ` (${it.variation})` : ""} × {it.quantity}</span>
                  <span>{money(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="border-t mt-4 pt-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{order.delivery_fee === 0 ? "Free" : money(order.delivery_fee)}</span></div>
              <div className="flex justify-between font-semibold text-base"><span>Total</span><span data-testid="confirm-total">{money(order.total)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Payment</span><span data-testid="confirm-payment-status">{order.payment_status}</span></div>
            </div>
            {order.delivery?.address && (
              <p className="text-sm text-muted-foreground mt-4 border-t pt-4">
                <strong className="text-foreground">Deliver to:</strong> {order.delivery.name}, {order.delivery.address}, {order.delivery.postcode}
                {order.delivery.date ? ` · ${order.delivery.date}` : ""}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="text-center mt-8">
        <Button asChild variant="outline" className="rounded-full" data-testid="back-to-shop-btn">
          <Link to="/shop">Back to the shop</Link>
        </Button>
      </div>
    </div>
  );
}
